import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, Stat } from "../../dashboard/primitives";
import { deleteLead, setLeadStatus, subscribeAllLeads } from "../../leads/api";
import { SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const STATUSES = ["new", "contacted", "closed", "spam"];

const PROJECT_LABELS = {
  "web-app": "Web app",
  ecommerce: "E-commerce",
  "mobile-app": "Mobile app",
  marketing: "Marketing site",
  "full-stack": "Full product",
  "just-looking": "Just browsing",
};

const BUDGET_LABELS = {
  starter: "$8,850+",
  mid: "$12.8k+",
  full: "$24.8k+",
  custom: "Not sure",
};

const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const LeadsSection = () => {
  const [leads, setLeads] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("active");

  useEffect(
    () =>
      subscribeAllLeads((v) => {
        setLeads(v);
        setLoaded(true);
      }),
    [],
  );

  const showSkeleton = useMinLoading(!loaded);

  const visible = useMemo(() => {
    if (filter === "all") return leads;
    if (filter === "active")
      return leads.filter((l) => !["closed", "spam"].includes(l.status));
    return leads.filter((l) => l.status === filter);
  }, [leads, filter]);

  const stats = useMemo(() => {
    const total = leads.length;
    const unread = leads.filter((l) => l.status === "new").length;
    const contacted = leads.filter((l) => l.status === "contacted").length;
    return { total, unread, contacted };
  }, [leads]);

  const handleDelete = (l) => {
    if (
      !window.confirm(
        `Delete lead from ${l.email || "—"}? This cannot be undone.`,
      )
    )
      return;
    return deleteLead(l.id);
  };

  return (
    <>
      <div className="fw-stat-grid">
        <Stat label="Leads" value={stats.total} loading={showSkeleton} />
        <Stat label="New" value={stats.unread} loading={showSkeleton} />
        <Stat
          label="Contacted"
          value={stats.contacted}
          loading={showSkeleton}
        />
      </div>

      <Card
        title={`Intake leads${filter === "new" ? " · new" : ""}`}
        action={
          <select
            className="fw-select"
            style={{ width: "auto" }}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
            <option value="spam">Spam</option>
            <option value="all">All</option>
          </select>
        }
      >
        {showSkeleton ? (
          <SkeletonList count={5} ariaLabel="Loading leads">
            <SkeletonRow cols={4} widths={["1fr", "100px", "80px", "80px"]} />
          </SkeletonList>
        ) : visible.length === 0 ? (
          <Empty>No leads</Empty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {visible.map((l) => {
              const expanded = expandedId === l.id;
              const isNew = l.status === "new";
              return (
                <div
                  key={l.id}
                  style={{
                    border: "1px solid var(--fw-line)",
                    borderLeft: isNew
                      ? "3px solid var(--fw-red)"
                      : "1px solid var(--fw-line)",
                    borderRadius: "var(--fw-r-md)",
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto auto auto",
                      gap: 12,
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>
                        {l.name || "—"}
                        {l.company ? ` · ${l.company}` : ""}
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--fw-mono)",
                          fontSize: 11,
                          color: "var(--fw-muted)",
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          marginTop: 2,
                        }}
                      >
                        {l.email || "no email"} ·{" "}
                        {PROJECT_LABELS[l.projectType] || l.projectType || "—"}{" "}
                        · {BUDGET_LABELS[l.budget] || "—"} ·{" "}
                        {fmtDate(l.createdAt)}
                      </div>
                    </div>
                    <select
                      className="fw-select"
                      style={{
                        width: "auto",
                        fontSize: 12,
                        padding: "4px 8px",
                      }}
                      value={l.status}
                      onChange={(e) => setLeadStatus(l.id, e.target.value)}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <button
                      className="fw-btn fw-btn--ghost"
                      style={{ padding: "6px 10px" }}
                      onClick={() => setExpandedId(expanded ? null : l.id)}
                    >
                      {expanded ? "Hide" : "Details"}
                    </button>
                    <button
                      className="fw-btn fw-btn--ghost"
                      style={{ padding: "6px 10px", color: "#c0392b" }}
                      onClick={() => handleDelete(l)}
                    >
                      Delete
                    </button>
                  </div>
                  {expanded && (
                    <div
                      style={{
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: "1px solid var(--fw-line)",
                        display: "grid",
                        gap: 10,
                        fontSize: 13,
                      }}
                    >
                      <Row
                        k="Platforms"
                        v={(l.platforms || []).join(", ") || "—"}
                      />
                      <Row
                        k="Features"
                        v={(l.features || []).join(", ") || "—"}
                      />
                      {l.featuresNote && (
                        <Row k="Specific ask" v={l.featuresNote} />
                      )}
                      <Row k="Timeline" v={l.timeline || "—"} />
                      <Row k="Referral" v={l.referral || "—"} />
                      {l.notes && <Row k="Notes" v={l.notes} />}
                      <Row
                        k="Source"
                        v={`${l.source || "—"}${
                          l.referrer ? ` · ref: ${l.referrer}` : ""
                        }`}
                      />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <a
                          className="fw-btn fw-btn--primary"
                          href={`mailto:${l.email}?subject=Re: your bboy creative brief`}
                          style={{
                            padding: "8px 14px",
                            textDecoration: "none",
                          }}
                        >
                          Reply →
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

const Row = ({ k, v }) => (
  <div className="fw-stack-sm" style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 12 }}>
    <div
      style={{
        fontFamily: "var(--fw-mono)",
        fontSize: 11,
        letterSpacing: 1.2,
        textTransform: "uppercase",
        color: "var(--fw-muted)",
      }}
    >
      {k}
    </div>
    <div style={{ color: "var(--fw-ink)" }}>{v}</div>
  </div>
);

export default LeadsSection;
